import type { AssetGroup, IconAsset, IconSourceData, Resolution, Slide } from '@lumen5/luminary';
import Luminary, { VideoData } from '@lumen5/luminary';

import type { AssetContainerModel } from 'js/src/client';
import { AssetContainerTypes, SourceEnum } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import {
    getSceneColorShuffleData,
    getSceneColorShuffleIndex,
    isSceneColorShuffleAvailable,
} from 'js/src/data/color-shuffle';
import {
    getActiveSearchContextForCurrentTab,
    isMediaSourceData,
    SEARCH_CONTEXT,
} from 'js/src/data/media';
import { getFocusedSubsceneIndex } from 'js/src/data/scenes';
import { getTemplateBundle } from 'js/src/data/templates';
import { immutableAssetContainerModelIsIcon } from 'js/src/data/uploads';
import { checkpointVideo } from 'js/src/data/video-data';
import { setObjectPropertyValue } from 'js/src/editor/util';
import type { ImmutableMap } from 'js/src/libs/consts';
import { SIMPLE_ICON_COLOR_THRESHOLD } from 'js/src/libs/consts'; // we consider icon as simple less than this number of colors
import { focusAssetElement } from 'js/src/libs/custom-utils';
import type { StartEditingNonTextAssetPayload } from 'js/src/libs/slide-event-emitter';
import SlideEventEmitter, { SLIDE_EVENTS } from 'js/src/libs/slide-event-emitter';
import uuid from 'js/src/libs/uuid';
import {
    addStickerAssetToSceneInternalOperation,
    deleteAssetOperation,
    getIconType,
    getOrCreateStickerCompatibleSceneOperation,
    getVoiceDrivenAssetSourceData,
    IconType,
    isTalkingHeadSceneGroup,
    saveRawLuminary,
} from 'js/src/luminary/index';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import { getElementPropertiesToSave } from 'js/src/luminary/luminary-utils';

/**
 * Update a color property of an icon asset
 */
export const setIconColorOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        assetId: string,
        colorPropertyName: string,
        newColor: number | string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) return;
        const asset = scene.getAssetById(assetId);
        if (asset && VideoData.isIconAsset(asset)) {
            setObjectPropertyValue(asset, colorPropertyName, newColor);
        }
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

/**
 * Assigns the provided iconSourceData object to the specified scene.
 * If no targetAssetId is specified we'll create the icon as a sticker on the scene
 * Creates a new scene if the scene is not compatible with an icon
 */
export const setIconAssetForSceneOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        sceneId: string,
        iconSourceData: ImmutableMap<AssetContainerModel>,
        {
            targetAssetId = null,
            targetPosition = null,
            targetDimensions = null,
            isDrag = false,
        }: {
            targetAssetId?: string | null;
            targetPosition?: { x: number; y: number } | null;
            targetDimensions?: Resolution | null;
            isDrag?: boolean;
        } = {},
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = call(
            getOrCreateStickerCompatibleSceneOperation,
            sceneId,
            systemTriggered,
            parentActionGroup,
        );
        if (!scene) {
            Toast.error('Unable to add icon to scene', 'Error');
            return;
        }

        if (!immutableAssetContainerModelIsIcon(iconSourceData)) {
            Toast.error('Invalid icon source data', 'Error');
            return;
        }

        const rawSourceData = iconSourceData.toJS();
        const iconAssetSourceData = rawSourceData.icon;
        const searchContext = getActiveSearchContextForCurrentTab(getState());

        const tryGetOrAddIconAsset = (scene: Slide) => {
            let iconAsset: IconAsset | null = null;

            const isSourceWaveform = searchContext === SEARCH_CONTEXT.WAVEFORMS;
            const targetAsset = targetAssetId ? scene.getAssetById(targetAssetId) : undefined;
            let introOutroTimes = null;
            let dynamicStyles = null;
            let groupIndex = null;
            let container: Slide | AssetGroup = scene;
            if (targetAsset) {
                if (VideoData.isIconAsset(targetAsset)) {
                    const swappable = isSourceWaveform
                        ? getIconType(targetAsset) === IconType.WAVEFORM
                        : true;
                    if (swappable) {
                        iconAsset = targetAsset;
                    }
                } else if (VideoData.isStickerAsset(targetAsset)) {
                    const assetGroup = targetAsset.getParentByType(VideoData.GROUP_ASSET_TYPE);
                    if (assetGroup) {
                        container = assetGroup;
                    }
                    ({
                        position: targetPosition,
                        dimensions: targetDimensions,
                        introOutroTimes,
                        dynamicStyles,
                        groupIndex,
                    } = getElementPropertiesToSave(targetAsset, scene.duration));
                    call(
                        deleteAssetOperation,
                        scene.id,
                        targetAsset.id,
                        systemTriggered,
                        parentActionGroup,
                    );
                    iconAsset = null;
                }
            }

            if (!iconAsset) {
                iconAsset = call(
                    addStickerAssetToSceneInternalOperation,
                    container,
                    VideoData.ICON_ASSET_TYPE,
                    {
                        position: targetPosition,
                        dimensions: targetDimensions,
                        introOutroTimes,
                        dynamicStyles,
                        groupIndex,
                    },
                );
            }

            if (iconAsset && isSourceWaveform) {
                if (isTalkingHeadSceneGroup(scene.sceneGroup)) {
                    const talkingHeadSourceData = getVoiceDrivenAssetSourceData(scene.sceneGroup);
                    if (isMediaSourceData(talkingHeadSourceData)) {
                        if (!talkingHeadSourceData.audio_url) {
                            const toastDuration = 10000;
                            Toast.error(
                                'Re-upload your video file to use the waveform',
                                'Something went wrong',
                                'waveform-missing-audio-id',
                                toastDuration,
                            );
                            analytics.track(
                                'Waveform added to talking head scene with missing audio_url',
                            );
                        }
                    }
                }
                iconAsset.playbackMode = VideoData.PLAYBACK_WAVEFORM;
                // TODO: Please remove the line below once luminary merges this ticket:
                // https://lumen5.atlassian.net/browse/LU-3877
                scene.requiresUpdate = true;
            }
            return iconAsset;
        };

        // get the icon to update
        const iconAsset = tryGetOrAddIconAsset(scene);
        if (!iconAsset) {
            Toast.error('Unable to add icon to scene', 'Error');
            return;
        }

        // assign sourceData
        if (
            iconAssetSourceData.num_colors === 0 &&
            iconAssetSourceData.source === SourceEnum.SOURCE_LUMEN5_ICONS_PREMIUM
        ) {
            // some of uploaded lumen5 icons white and doesn't have num_colors value in DB, so we need to replace sourceData to apply existing colorMap
            iconAsset.replace(iconAssetSourceData as IconSourceData); // will use old colorMap
        } else if (
            iconAssetSourceData.num_colors < SIMPLE_ICON_COLOR_THRESHOLD &&
            iconAsset.colorMaps.length === iconAssetSourceData.num_colors
        ) {
            // basic case: simple icon replace with similar simple icon
            iconAsset.replace(iconAssetSourceData as IconSourceData); // if icon has less than 3 colors rewrite colors with old colorMap
        } else if (
            iconAssetSourceData.num_colors < SIMPLE_ICON_COLOR_THRESHOLD &&
            iconAsset.colorMaps.length > SIMPLE_ICON_COLOR_THRESHOLD
        ) {
            const state = getState();
            const isColorShuffleAvaliable = isSceneColorShuffleAvailable(state, sceneId);
            // we're using a "data" Luminary instance, an instance of Luminary which we're configuring to be fast so we can use it to pull info
            // Even with these optimizations it takes 30-50ms to create so we shouldn't abuse it!
            const dataLuminary = new Luminary({
                fetchResources: false,
                disableRenderer: true,
                autoDraw: false,
                autoUpdate: false,
            });

            const templateBundle = getTemplateBundle(state, luminary.template.id).toJS();

            dataLuminary.setTemplate(templateBundle);

            const targetSceneDesignId = scene.sceneDesign;
            const dataScene = dataLuminary.addScene({
                sceneDesignId: targetSceneDesignId,
            });

            // now we can just pull data from the data scene!
            const templateIconAsset = dataScene.getAssetByName(iconAsset.name) as IconAsset;
            if (templateIconAsset) {
                let iconIndexColor = VideoData.getTemplateIndexFromAssetProperty(
                    templateIconAsset,
                    'colorMaps.0.targetColor',
                );

                if (isColorShuffleAvaliable) {
                    const colorShuffleIndex = getSceneColorShuffleIndex(state, sceneId);
                    const colorShuffleData = getSceneColorShuffleData(state, sceneId);
                    if (colorShuffleData) {
                        iconIndexColor =
                            colorShuffleData.colorShuffles[colorShuffleIndex][iconIndexColor];
                    }
                }
                setObjectPropertyValue(
                    iconAsset,
                    'colorMaps[0].targetColor',
                    VideoData.templateColorByIndex(iconIndexColor),
                ); // we modify old map to use original color from template and then replace icons.
            }

            iconAsset.replace(iconAssetSourceData as IconSourceData);

            void dataLuminary.destroy();
        } else {
            iconAsset.sourceData = iconAssetSourceData as IconSourceData; // this will create new colorMap from icon using custom colors
            iconAsset.slide.attribution = iconAssetSourceData.attribution ?? '';
        }

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        // analytics
        analytics.track('Icon Added To Scene', {
            sceneId: sceneId,
            method: isDrag ? 'drag' : 'click',
            media_source: searchContext,
            current_user: iconSourceData
                .get(AssetContainerTypes.ICON)
                .get('history_index_user_ids')
                ?.some((user_id: number) => SERVER.USER.id === user_id),
            is_waveform: searchContext === SEARCH_CONTEXT.WAVEFORMS,
        });
        if (!systemTriggered) {
            const subsceneIndex = getFocusedSubsceneIndex(getState());
            SlideEventEmitter.emit(SLIDE_EVENTS.START_EDITING_NON_TEXT_ASSET, {
                sceneGroupId: scene.sceneGroup.id,
                sceneId,
                subsceneIndex,
                assetId: iconAsset.id,
                assetType: VideoData.ICON_ASSET_TYPE,
            } as StartEditingNonTextAssetPayload);
            focusAssetElement(iconAsset.id, sceneId, subsceneIndex ?? 0);
        }
    },
);

/**
 * Fetches the specified asset from a scene and functionally "empties" its source data so
 * it doesn't display anything.
 */
export const clearIconAssetSourceDataOperation = createLuminaryOperation(
    (
        { luminary, dispatch },
        sceneId: string,
        assetId: string,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return;
        }

        const iconAsset = scene.getAssetById(assetId) as IconAsset | undefined;

        if (!iconAsset) {
            return;
        }

        if (!VideoData.isIconAsset(iconAsset)) {
            throw new Error('Provided asset is not an icon.');
        }

        iconAsset.sourceData = VideoData.INVISIBLE_LOTTIE;

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        analytics.track('Icon Asset cleared', { sceneId });
    },
);
